import { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { routes } from "@/app/navigation/routes";
import {
  paymentSubscriptionService,
  type CheckoutStatusData,
  type PaymentCheckoutStatusItem,
} from "@/features/protest/services";
import { toastService } from "@/shared/toast";
import { tokenService } from "@/services/auth/token.service";
import { propertyService } from "@/features/properties/model/propertyServices";
import type { PropertyApiResponse } from "@/features/properties/model/propertiesTypes";
import { usePropertyStreetViews } from "@/shared/utils";
import paymentHeroCelebrationImg from "@/assets/images/payment-hero-celebration.jpg";
import propFallback1 from "@/assets/images/prop-fallback-1.jpg";
import propFallback2 from "@/assets/images/prop-fallback-2.jpg";
import styles from "./PaymentSuccessPage.module.css";

// ============================================================================
// SVG Icons Matching Mockup
// ============================================================================

const CheckBadgeIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const CreditCardIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <line x1="2" y1="10" x2="22" y2="10" />
  </svg>
);

const HouseIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

const ShieldCheckIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

const CalendarIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const PinIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const DollarIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

const ClockIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const LockIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const HeadsetIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
    <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
  </svg>
);

const MailIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </svg>
);

const ReceiptIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16l3-1.5 3 1.5 3-1.5 3 1.5 3-1.5 3 1.5V8z" />
    <path d="M14 2v6h6" />
    <line x1="8" y1="13" x2="16" y2="13" />
    <line x1="8" y1="17" x2="13" y2="17" />
  </svg>
);

const fallbackImages = [propFallback1, propFallback2];

export const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const sessionId = searchParams.get("session_id");

  const [isLoading, setIsLoading] = useState(true);
  const [checkoutStatus, setCheckoutStatus] = useState<CheckoutStatusData | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [propertyAddresses, setPropertyAddresses] = useState<Record<string, string>>({});
  const [propertiesMap, setPropertiesMap] = useState<Record<string, PropertyApiResponse>>({});
  const [yearDropdownOpen, setYearDropdownOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState<string>("2026");

  // Fetch full details of each property for dynamic images and addresses
  const fetchCheckoutStatus = useCallback(
    async (sid: string | null) => {
      if (!sid) {
        // Check if state was passed from guest flow (e.g. from HomePage "Create your account" card)
        const navState = location.state as
          | {
              property?: PropertyApiResponse;
              email?: string;
              propertyId?: string;
              year?: string | number;
              autoOpenRegister?: boolean;
            }
          | undefined;

        const stateProp = navState?.property;
        const stateEmail = navState?.email || "";
        const stateYear = Number(navState?.year || 2027);

        if (stateProp || stateEmail) {
          const propId = stateProp?.property_id || navState?.propertyId || "prop_guest";
          const paymentIntentId =
            stateProp?.protestPayments?.[stateYear]?.paymentIntentId || "pi_guest_paid";
          const mockStatus: CheckoutStatusData = {
            checkout_session_id: paymentIntentId,
            checkout_status: "paid",
            is_complete: true,
            paid_count: 1,
            total_count: 1,
            customer_email: stateEmail || stateProp?.protestPayments?.[stateYear]?.email || "",
            payments: [
              {
                payment_id: 1,
                property_id: propId,
                user_id: null,
                email: stateEmail || stateProp?.protestPayments?.[stateYear]?.email || null,
                year: stateYear,
                amount: stateProp?.protestPayments?.[stateYear]?.amount || 49,
                final_amount: stateProp?.protestPayments?.[stateYear]?.amount || 49,
                paid: true,
                status: "paid",
                paid_at:
                  stateProp?.protestPayments?.[stateYear]?.paidAt || new Date().toISOString(),
                subscription_id: null,
                stripe_checkout_session_id: paymentIntentId,
              },
            ],
          };
          setCheckoutStatus(mockStatus);
          if (stateProp && propId) {
            setPropertiesMap({ [propId]: stateProp });
            const addr =
              stateProp.address?.full_address ||
              stateProp.address?.street ||
              stateProp.address?.line1 ||
              "";
            if (addr) {
              setPropertyAddresses({ [propId]: addr });
            }
          }
          setIsLoading(false);
          setErrorMsg(null);
          return;
        }

        setErrorMsg("No session ID found in payment URL.");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setErrorMsg(null);
      setCheckoutStatus(null);

      try {
        const encoded = encodeURIComponent(sid);
        const res = await paymentSubscriptionService.getCheckoutStatus(encoded);
        const statusPayload =
          (res as unknown as { data?: { data?: CheckoutStatusData } })?.data?.data ||
          (res as unknown as { data?: CheckoutStatusData })?.data ||
          (res as unknown as CheckoutStatusData);

        if (statusPayload) {
          setCheckoutStatus(statusPayload);
          if (statusPayload.checkout_status === "paid" || statusPayload.is_complete) {
            toastService.show("Payment completed successfully!", "success");
          }

          if (statusPayload.payments && statusPayload.payments.length > 0) {
            const fetchPropertiesData = async () => {
              const addresses: Record<string, string> = {};
              const propMap: Record<string, PropertyApiResponse> = {};
              await Promise.all(
                statusPayload.payments.map(async (payment: PaymentCheckoutStatusItem) => {
                  try {
                    const property = await propertyService.getPropertyById(payment.property_id);
                    if (property) {
                      propMap[payment.property_id] = property;
                      if (property.address?.full_address) {
                        addresses[payment.property_id] = property.address.full_address;
                      }
                    }
                  } catch (err) {
                    console.error("Failed to fetch property details for", payment.property_id, err);
                  }
                })
              );
              setPropertyAddresses(addresses);
              setPropertiesMap(propMap);
            };
            void fetchPropertiesData();
          }
        } else {
          setErrorMsg("Could not retrieve checkout status details.");
        }
      } catch (err) {
        console.error("Failed to fetch checkout status:", err);
        setErrorMsg("Failed to verify payment status with server.");
      } finally {
        setIsLoading(false);
      }
    },
    [location.state]
  );

  useEffect(() => {
    let cancelled = false;
    if (cancelled) return;
    void fetchCheckoutStatus(sessionId);
    return () => {
      cancelled = true;
    };
  }, [sessionId, fetchCheckoutStatus]);

  // Hook for dynamic Google Maps Street View images
  const propertyList = useMemo(() => {
    return Object.values(propertiesMap);
  }, [propertiesMap]);

  const streetViewImages = usePropertyStreetViews(propertyList);

  // Available tax years for dropdown
  const availableYears = useMemo(() => {
    if (!checkoutStatus?.payments || checkoutStatus.payments.length === 0) {
      return ["2026"];
    }
    const years = Array.from(new Set(checkoutStatus.payments.map((p) => String(p.year || "2026"))));
    return years.sort().reverse();
  }, [checkoutStatus]);

  useEffect(() => {
    if (availableYears.length > 0 && availableYears[0] && !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears, selectedYear]);

  const filteredPayments = useMemo(() => {
    if (!checkoutStatus?.payments) return [];
    if (!selectedYear || selectedYear === "All") return checkoutStatus.payments;
    const filtered = checkoutStatus.payments.filter((p) => String(p.year) === selectedYear);
    return filtered.length > 0 ? filtered : checkoutStatus.payments;
  }, [checkoutStatus, selectedYear]);

  const handleContinue = () => {
    const allPaymentProps = (checkoutStatus?.payments || []).map((p) => p.property_id);
    const filterPaidProps = (checkoutStatus?.payments || [])
      .filter((p) => {
        const s = (p.status || "").toLowerCase();
        return p.paid || s === "paid" || s === "complete" || s === "succeeded";
      })
      .map((p) => p.property_id);
    const mapProps = Object.keys(propertiesMap);

    const paidPropertyIds = Array.from(
      new Set(
        (filterPaidProps.length > 0
          ? filterPaidProps
          : allPaymentProps.length > 0
            ? allPaymentProps
            : mapProps
        ).filter(Boolean)
      )
    );

    const paidPropertiesList = paidPropertyIds
      .map((id) => propertiesMap[id])
      .filter((p): p is PropertyApiResponse => Boolean(p));

    const customerEmail =
      checkoutStatus?.customer_email || checkoutStatus?.payments?.find((p) => p.email)?.email || "";

    const state = {
      paidPropertyIds,
      paidProperties: paidPropertiesList,
      customerEmail,
      isGuest: !tokenService.hasToken(),
    };

    try {
      localStorage.setItem("protest_paid_property_ids", JSON.stringify(paidPropertyIds));
      if (customerEmail) {
        localStorage.setItem("protest_customer_email", customerEmail);
      }
    } catch (e) {
      console.error("Failed to store protest details in localStorage:", e);
    }

    navigate(routes.selectPropertyType, { state });
  };

  const handleGoToProperties = () => {
    navigate(routes.properties);
  };

  const handleCopySessionId = (id: string) => {
    if (!id) return;
    navigator.clipboard
      .writeText(id)
      .then(() => {
        toastService.show("Session ID copied to clipboard!", "success");
      })
      .catch(() => {
        toastService.show("Failed to copy Session ID", "error");
      });
  };

  const paidCount = checkoutStatus?.paid_count || checkoutStatus?.payments?.length || 0;
  const totalCount = checkoutStatus?.total_count || checkoutStatus?.payments?.length || 0;

  return (
    <div className={styles.container}>
      <div className={styles.ambientLeavesLeft} aria-hidden="true" />
      <div className={styles.ambientLeavesRight} aria-hidden="true" />

      <main className={styles.mainWrapper}>
        {isLoading ? (
          <div className={styles.card}>
            <div className={styles.loadingWrap}>
              <div className={styles.spinner} />
              <p className={styles.loadingText}>Verifying payment status with server...</p>
            </div>
          </div>
        ) : errorMsg && !checkoutStatus ? (
          <div className={styles.card}>
            <div className={styles.errorWrap}>
              <div className={styles.badgeError}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  width="28"
                  height="28"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h2 className={styles.errorTitle}>Payment Status Verification</h2>
              <p className={styles.subtitle}>{errorMsg}</p>

              {sessionId && (
                <div className={styles.detailsBox}>
                  <div className={styles.detailRow}>
                    <div className={styles.detailLabelGroup}>
                      <span className={styles.detailLabel}>Session ID</span>
                    </div>
                    <div className={styles.sessionIdWrap}>
                      <span className={styles.detailValueTruncated} title={sessionId}>
                        {sessionId}
                      </span>
                      <button
                        type="button"
                        className={styles.copyBtn}
                        onClick={() => handleCopySessionId(sessionId)}
                        title="Copy Session ID"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className={styles.actions}>
                <button type="button" className={styles.btnPrimary} onClick={handleContinue}>
                  Continue to File Protest
                </button>
                <button
                  type="button"
                  className={styles.btnSecondary}
                  onClick={handleGoToProperties}
                >
                  View My Properties
                </button>
                <button
                  type="button"
                  className={styles.btnSecondary}
                  onClick={() => fetchCheckoutStatus(sessionId)}
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.contentStack}>
            {/* ============================================================= */}
            {/* 1. TOP HERO SECTION (Matching Mockup)                        */}
            {/* ============================================================= */}
            <section className={styles.heroSection}>
              <div className={styles.heroLeftCol}>
                <div className={styles.transactionBadge}>
                  <CheckBadgeIcon />
                  <span>Transaction Completed</span>
                </div>
                <h1 className={styles.heroTitle}>
                  <span>Payment </span>
                  <span className={styles.heroTitleHighlight}>Successful!</span>
                </h1>
                <p className={styles.heroSubtitle}>
                  Your property tax protest payment has been{" "}
                  <strong className={styles.greenHighlight}>confirmed and recorded.</strong>
                </p>
                <p className={styles.heroSubtext}>
                  Thank you for your payment. You&apos;re one step closer to potential property tax
                  savings!
                </p>
              </div>

              <div className={styles.heroRightGraphic}>
                <img
                  src={paymentHeroCelebrationImg}
                  alt="Modern contemporary house with verified celebration"
                  className={styles.heroHouseImg}
                />
              </div>
            </section>

            {/* ============================================================= */}
            {/* 2. 3-COLUMN SUMMARY STRIP CARD                               */}
            {/* ============================================================= */}
            <section className={styles.summaryStripCard}>
              <div className={styles.summaryCol}>
                <div className={styles.summaryIconCircle}>
                  <CreditCardIcon />
                </div>
                <div className={styles.summaryInfo}>
                  <span className={styles.summaryLabel}>Payment Status</span>
                  <div className={styles.statusPill}>
                    <CheckBadgeIcon />
                    <span>{checkoutStatus?.checkout_status || "PAID"}</span>
                  </div>
                  <span className={styles.summarySubtext}>
                    Your payment was processed successfully.
                  </span>
                </div>
              </div>

              <div className={styles.summaryDivider} aria-hidden="true" />

              <div className={styles.summaryCol}>
                <div className={styles.summaryIconCircle}>
                  <HouseIcon />
                </div>
                <div className={styles.summaryInfo}>
                  <span className={styles.summaryLabel}>Properties Paid</span>
                  <span className={styles.summaryValueText}>
                    {paidCount} of {totalCount}
                  </span>
                  <span className={styles.summarySubtext}>All set! You&apos;re good to go.</span>
                </div>
              </div>

              <div className={styles.summaryDivider} aria-hidden="true" />

              <div className={styles.summaryCol}>
                <div className={styles.summaryIconCircle}>
                  <ShieldCheckIcon />
                </div>
                <div className={styles.summaryInfo}>
                  <span className={styles.summaryLabel}>Payment Secure</span>
                  <span className={styles.summarySubtext}>
                    Your transaction is safe and encrypted.
                  </span>
                </div>
              </div>
            </section>

            {/* ============================================================= */}
            {/* 3. PAID PROPERTIES CARD CONTAINER                            */}
            {/* ============================================================= */}
            {checkoutStatus?.payments && checkoutStatus.payments.length > 0 && (
              <section className={styles.paidPropertiesCard}>
                <div className={styles.paidPropertiesHeader}>
                  <div className={styles.paidPropertiesTitleGroup}>
                    <div className={styles.titleIconCircle}>
                      <HouseIcon />
                    </div>
                    <div>
                      <h2 className={styles.paidPropertiesTitle}>Paid Properties ({paidCount})</h2>
                      <p className={styles.paidPropertiesSubtext}>
                        Here are the details of the properties you have paid for.
                      </p>
                    </div>
                  </div>

                  {/* Year Dropdown Pill */}
                  <div className={styles.yearDropdownWrap}>
                    <button
                      type="button"
                      className={styles.yearPillButton}
                      onClick={() => setYearDropdownOpen((prev) => !prev)}
                      aria-haspopup="listbox"
                      aria-expanded={yearDropdownOpen}
                    >
                      <CalendarIcon />
                      <span>{selectedYear}</span>
                      <ChevronDownIcon />
                    </button>
                    {yearDropdownOpen && (
                      <div className={styles.yearMenu}>
                        {availableYears.map((yr) => (
                          <button
                            key={yr}
                            type="button"
                            className={`${styles.yearMenuItem} ${
                              selectedYear === yr ? styles.yearMenuItemActive : ""
                            }`}
                            onClick={() => {
                              setSelectedYear(yr);
                              setYearDropdownOpen(false);
                            }}
                          >
                            {yr}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* 2-Column Properties Grid */}
                <div className={styles.propertiesGrid}>
                  {filteredPayments.map((p: PaymentCheckoutStatusItem, index: number) => {
                    const fullAddr = propertyAddresses[p.property_id] || "";
                    const streetLine = fullAddr
                      ? fullAddr.split(",")[0]
                      : `Property ${p.property_id.substring(0, 5)}`;
                    const displayImage =
                      streetViewImages[p.property_id] ||
                      fallbackImages[index % fallbackImages.length];

                    return (
                      <article className={styles.propertyCard} key={p.payment_id}>
                        {/* Top: Property Photo + Address & Status */}
                        <div className={styles.propCardTop}>
                          <div className={styles.propImageWrap}>
                            <img
                              src={displayImage}
                              alt={streetLine}
                              className={styles.propImage}
                              onError={(e) => {
                                const target = e.currentTarget;
                                if (target.src !== propFallback1) {
                                  target.src = propFallback1;
                                }
                              }}
                            />
                          </div>

                          <div className={styles.propInfoWrap}>
                            <div className={styles.propHeadingRow}>
                              <h3 className={styles.propTitle} title={streetLine}>
                                {streetLine}
                              </h3>
                              <span className={styles.propPaidBadge}>
                                <CheckBadgeIcon />
                                <span>{p.paid ? "PAID" : p.status.toUpperCase()}</span>
                              </span>
                            </div>

                            <p
                              className={styles.propFullAddress}
                              title={fullAddr || `ID: ${p.property_id}`}
                            >
                              <PinIcon />
                              <span>{fullAddr || `Property ID: ${p.property_id}`}</span>
                            </p>
                          </div>
                        </div>

                        {/* Bottom: 4 Meta Fields */}
                        <div className={styles.propCardMetaGrid}>
                          <div className={styles.metaCol}>
                            <span className={styles.metaLabel}>
                              <CalendarIcon />
                              <span>Year</span>
                            </span>
                            <span className={styles.metaValue}>{p.year}</span>
                          </div>

                          <div className={styles.metaCol}>
                            <span className={styles.metaLabel}>
                              <DollarIcon />
                              <span>Amount</span>
                            </span>
                            <span className={styles.metaValue}>
                              ${p.final_amount?.toFixed?.(2) ?? p.amount}
                            </span>
                          </div>

                          <div className={styles.metaCol}>
                            <span className={styles.metaLabel}>
                              <CheckBadgeIcon />
                              <span>Status</span>
                            </span>
                            <span className={`${styles.metaValue} ${styles.statusTextGreen}`}>
                              {p.paid ? "Paid" : p.status}
                            </span>
                          </div>

                          <div className={styles.metaCol}>
                            <span className={styles.metaLabel}>
                              <ClockIcon />
                              <span>Paid At</span>
                            </span>
                            <span className={styles.metaValue}>
                              {p.paid_at
                                ? new Date(p.paid_at).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  })
                                : "Sep 8, 2026"}
                            </span>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>

                {/* Continue to File Protest Button */}
                <div className={styles.ctaButtonWrapper}>
                  <button type="button" className={styles.btnContinue} onClick={handleContinue}>
                    <ArrowRightIcon />
                    <span>Continue to File Protest</span>
                  </button>
                </div>
              </section>
            )}

            {/* ============================================================= */}
            {/* 4. FOOTER 4-COLUMN INFO BAR                                  */}
            {/* ============================================================= */}
            <footer className={styles.footerInfoBar}>
              <div className={styles.footerCol}>
                <div className={styles.footerIconCircle}>
                  <LockIcon />
                </div>
                <div className={styles.footerColText}>
                  <h4 className={styles.footerTitle}>Secure & Encrypted</h4>
                  <p className={styles.footerSubtext}>
                    Your payment is protected with bank-level security.
                  </p>
                </div>
              </div>

              <div className={styles.footerDivider} aria-hidden="true" />

              <div className={styles.footerCol}>
                <div className={styles.footerIconCircle}>
                  <HeadsetIcon />
                </div>
                <div className={styles.footerColText}>
                  <h4 className={styles.footerTitle}>Need Help?</h4>
                  <p className={styles.footerSubtext}>Our support team is here to assist you.</p>
                </div>
              </div>

              <div className={styles.footerDivider} aria-hidden="true" />

              <div className={styles.footerCol}>
                <div className={styles.footerIconCircle}>
                  <MailIcon />
                </div>
                <div className={styles.footerColText}>
                  <h4 className={styles.footerTitle}>Contact Support</h4>
                  <p className={styles.footerSubtext}>
                    <a href="mailto:support@andyapp.com" className={styles.footerLink}>
                      support@andyapp.com
                    </a>
                    <br />
                    +1 (800) 123-4567
                  </p>
                </div>
              </div>

              <div className={styles.footerDivider} aria-hidden="true" />

              <div className={styles.footerCol}>
                <div className={styles.footerIconCircle}>
                  <ReceiptIcon />
                </div>
                <div className={styles.footerColText}>
                  <h4 className={styles.footerTitle}>Payment Receipt</h4>
                  <p className={styles.footerSubtext}>
                    A receipt has been sent to your registered email.
                  </p>
                </div>
              </div>
            </footer>
          </div>
        )}
      </main>
    </div>
  );
};
